import { useWorkareaPricingInvalidator } from './workareaPricingQueries'
import { useMutation } from "react-query"
import { createWorkarea, CreateWorkareaRequest, deleteWorkarea, duplicateWorkarea, updateWorkarea, WorkareaUpdate, updateRecurringSchedule, syncRecurringWorkareas } from "./workareaApi"
import { notification } from "antd"
import { DuplicateForm } from "../../components/jobs/JobPlan/EditorDrawer/Sidebar/DuplicateWorkareaModal"
import { RecurringSchedule } from "../recurringSchedule"
import { useJobItemsInvalidator } from "../../components/Items/itemQueries"
import { useWorkareaGroupsInvalidator, useWorkareaGroupsOptimisticUpdate } from "./groupsQueries"
import { Workarea } from "../job"

export type WorkareaMutationData = {
  isValidUpdate: boolean
  optimisticUpdate: Partial<Workarea>
  dataForServer: WorkareaUpdate
}

export const useUpdateWorkareaMutation = () => {
  const workareaGroupsOptimisticUpdate = useWorkareaGroupsOptimisticUpdate()
  const pricingInvalidator = useWorkareaPricingInvalidator()
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()

  return useMutation({
    mutationFn: ({ dataForServer }: WorkareaMutationData) => updateWorkarea(dataForServer),
    onMutate: ({ optimisticUpdate, dataForServer }) => {
      workareaGroupsOptimisticUpdate.setWorkareaOptimisticUpdate(dataForServer.jobId, optimisticUpdate)
    },
    onSuccess: (_, { dataForServer }) => {
      workareaGroupsInvalidator.invalidateForJob(dataForServer.jobId)
      pricingInvalidator.invalidateForJob(dataForServer.jobId)
    },
    onError: (_, parameters) => {
      notification.error({ message: 'An error occurred while updating the workarea' })
      workareaGroupsInvalidator.invalidateForJob(parameters.dataForServer.jobId)
    }
  })
}

export const useDeleteWorkareaMutation = (onSuccess: () => void) => {
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()

  return useMutation({
    mutationFn: ({ workareaId }: { workareaId: number, jobId: number }) => deleteWorkarea(workareaId),
    onSuccess: (_, { jobId }) => {
      workareaGroupsInvalidator.invalidateForJob(jobId)
      onSuccess()
    },
    onError: (error: any) => {
      const errorMessage = 'Error deleting workarea: ' + error.response?.data?.message || 'An error occurred while deleting the workarea'
      notification.error({ message: errorMessage })
    }
  })
}

export type DuplicateMutationData = {
  workareaId: number
  jobId: number
  form: DuplicateForm
}

export const useDuplicateWorkareaMutation = (onSuccess: (workareaId: number) => void) => {
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()
  const jobItemsInvalidator = useJobItemsInvalidator()

  return useMutation({
    mutationFn: (data: DuplicateMutationData) => duplicateWorkarea(data),
    onSuccess: (workareaId, { jobId, form }) => {
      workareaGroupsInvalidator.invalidateForJob(jobId)
      if (form.copyItems) {
        jobItemsInvalidator.invalidateForJob(jobId)
      }
      onSuccess(workareaId)
    },
    onError: (error: any) => {
      const errorMessage = 'Error duplicating workarea: ' + error.response?.data?.message || 'An error occurred while duplicating the workarea'
      notification.error({ message: errorMessage })
    }
  })
}

export const useCreateWorkareaMutation = (onSuccess: (newWorkareaId: number) => void) => {
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()

  return useMutation({
    mutationFn: (request: CreateWorkareaRequest) => createWorkarea(request),
    onSuccess: (newWorkareaId, { jobId }) => {
      workareaGroupsInvalidator.invalidateForJob(jobId)
      onSuccess(newWorkareaId)
    },
    onError: (error: any) => {
      const errorMessage = 'Error creating workarea: ' + error.response?.data?.message || 'An error occurred while creating the workarea'
      notification.error({ message: errorMessage })
    }
  })
}

export const useUpdateRecurringScheduleMutation = () => {
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()

  return useMutation({
    mutationFn: ({ workareaId, schedule }: { jobId: number, workareaId: number, schedule: RecurringSchedule }) => updateRecurringSchedule(workareaId, schedule),
    onSuccess: (_, parameters) => {
      workareaGroupsInvalidator.invalidateForJob(parameters.jobId)
    },
    onError: () => {
      notification.error({ message: 'An error occurred while updating the recurring schedule' })
    }
  })
}

export const useSyncRecurringWorkareasMutation  = (onFinished: () => void) => {
  const jobItemsInvalidator = useJobItemsInvalidator()
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()

  return useMutation({
    mutationFn: syncRecurringWorkareas,
    onSuccess: (_, parameters) => {
      workareaGroupsInvalidator.invalidateForJob(parameters.jobId)
      jobItemsInvalidator.invalidateForJob(parameters.jobId)
      onFinished()
    },
    onError: (error: any, parameters) => {
      const errorMessage = error.response?.data?.message || 'An error occurred while syncing recurring workareas.'
      notification.error({ message: errorMessage })
      workareaGroupsInvalidator.invalidateForJob(parameters.jobId)
      jobItemsInvalidator.invalidateForJob(parameters.jobId)
      onFinished()
    }
  })
}